/*
 * The two notification routes (protocol.md §3.2 "Notifications"), and the one rule that matters
 * about them: what comes back is written into the model, and nothing is written into the model
 * before it does.
 *
 * The five preferences live in the model already — the bootstrap carries them and
 * `notify.prefs.changed` replaces them when another device changes one — so the settings screen
 * renders the model rather than state of its own. A refused write therefore leaves the screen
 * showing what the server still holds, which is the honest answer: a switch that flips and then
 * flips back is worse than one that does not move until it has.
 */

import { api } from "../net/api.ts"
import type { NotificationPrefView } from "../store/model.ts"
import { client } from "../store/store.ts"

/** `PUT /api/notifications/prefs`: every field optional, an absent one left as it is. */
export type NotificationPrefPatch = Partial<NotificationPrefView>

/** The mail policies the server accepts (`internal/core/notify/prefs.go`). */
export const MAIL_POLICY_OFF = "off"
export const MAIL_POLICY_MENTIONS_DMS = "mentions_dms"

/**
 * The delays the server's validator allows, as the screen offers them: 2…240 minutes. The range is
 * the validator's (`validateDelay`) and these six are the steps worth a row in a menu.
 */
export const EMAIL_DELAY_CHOICES = [2, 5, 10, 30, 60, 240] as const

export const notifyApi = {
	/** The caller's own five preferences. Used by a screen that opens without a bootstrap. */
	read(): Promise<NotificationPrefView> {
		return api.get<NotificationPrefView>("/api/notifications/prefs").then(store)
	},

	/** Changes some of them, in one transaction, and returns the whole state that resulted. */
	write(patch: NotificationPrefPatch): Promise<NotificationPrefView> {
		return api.put<NotificationPrefView>("/api/notifications/prefs", patch).then(store)
	},
}

/** Puts the server's answer into the model, where every surface reads it from. */
function store(view: NotificationPrefView): NotificationPrefView {
	client.api.setState(() => ({ notificationPrefs: view }))
	return view
}
